// AGC: FarmProHub Wallet Funds Page
// AGC: Displays ETH, USDC, and FarmPro/OGB balances plus payment history from revenue-sharing

import React, { useState, useEffect } from 'react'
import { ArrowLeft, ExternalLink, Wallet } from 'lucide-react'
import { formatEther } from 'viem'
import { useAccount, useChainId, useBalance, useReadContract } from 'wagmi'
import { motion } from 'framer-motion'
import { parseAbi } from 'viem'

// AGC: OGB Token ABI
const OGB_TOKEN_ABI = parseAbi([
  'function balanceOf(address account) external view returns (uint256)',
])

// AGC: USDC Token ABI
const USDC_TOKEN_ABI = parseAbi([
  'function balanceOf(address account) external view returns (uint256)',
])

interface PaymentHistoryItem {
  id: string
  type: 'revenue_share' | 'staking_reward' | 'proposal_reward'
  amount: string
  token: 'ETH' | 'USDC' | 'FP'
  timestamp: number
  txHash?: string
  description: string
}

interface WalletFundsPageProps {
  onBack?: () => void
}

export const WalletFundsPage: React.FC<WalletFundsPageProps> = ({ onBack }) => {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)

  // AGC: Get ETH balance
  const { data: ethBalance } = useBalance({
    address: address,
    query: {
      enabled: !!address && isConnected,
    },
  })

  // AGC: Get OGB/FarmPro balance
  const OGB_TOKEN_ADDRESS = (import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
  const { data: ogbBalance } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: OGB_TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConnected && OGB_TOKEN_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Get USDC balance (official USDC - Base Sepolia or Mainnet based on chainId)
  const USDC_TOKEN_ADDRESS = (chainId === 8453 
    ? (import.meta.env.VITE_USDC_ADDRESS_MAINNET || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913')
    : (import.meta.env.VITE_USDC_ADDRESS || '0x036CbD53842c5426634e7929541eC2318f3dCF7e')
  ) as `0x${string}`
  const { data: usdcBalance } = useReadContract({
    address: USDC_TOKEN_ADDRESS,
    abi: USDC_TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConnected && USDC_TOKEN_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Load payment history from revenue-sharing
  useEffect(() => {
    if (!address) return

    const loadPaymentHistory = async () => {
      setIsLoadingHistory(true)
      try {
        // AGC: TODO: Fetch payment history from backend API
        // const response = await fetch(`${backendUrl}/api/revenue/payments/${address}`)
        // const data = await response.json()
        // setPaymentHistory(data.payments || [])
        
        // AGC: Mock data for now
        setPaymentHistory([])
      } catch (error) {
        console.error('AGC: Error loading payment history:', error)
        setPaymentHistory([])
      } finally {
        setIsLoadingHistory(false)
      }
    }

    loadPaymentHistory()
  }, [address])

  // AGC: Format balance for display
  const formatBalance = (balance: bigint | undefined, decimals: number = 18, symbol: string) => {
    if (!balance) return `0.00 ${symbol}`
    const formatted = formatEther(balance)
    const num = parseFloat(formatted)
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M ${symbol}`
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K ${symbol}`
    return `${num.toFixed(4)} ${symbol}`
  }

  // AGC: Format USDC balance (6 decimals)
  const formatUSDCBalance = (balance: bigint | undefined) => {
    if (!balance) return '0.00 USDC'
    const formatted = (Number(balance) / 1e6).toFixed(2)
    return `${formatted} USDC`
  }

  // AGC: Get block explorer URL
  const getExplorerUrl = (txHash: string) => {
    if (chainId === 8453) return `https://basescan.org/tx/${txHash}`
    if (chainId === 84532) return `https://sepolia.basescan.org/tx/${txHash}`
    return `https://basescan.org/tx/${txHash}`
  }

  // AGC: Format timestamp
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 py-4"
        >
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <Wallet className="w-7 h-7 text-[#0072CE]" />
            <h1 className="text-2xl font-bold text-white">Wallet Funds</h1>
          </div>
        </motion.div>

        {/* AGC: Balances Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {/* AGC: ETH Balance */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-[#0072CE] rounded-full flex items-center justify-center text-xs font-bold text-white">
                Ξ
              </div>
              <span className="text-slate-400 text-sm font-semibold">ETH</span>
            </div>
            <p className="text-white text-2xl font-bold">
              {ethBalance ? formatBalance(ethBalance.value, 18, 'ETH') : '0.0000 ETH'}
            </p>
            <p className="text-slate-500 text-xs mt-2">For gas fees</p>
          </div>

          {/* AGC: USDC Balance */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-[#0072CE] rounded-full flex items-center justify-center text-xs font-bold text-white">
                $
              </div>
              <span className="text-slate-400 text-sm font-semibold">USDC</span>
            </div>
            <p className="text-white text-2xl font-bold">
              {formatUSDCBalance(usdcBalance)}
            </p>
            <p className="text-slate-500 text-xs mt-2">Stablecoin</p>
          </div>

          {/* AGC: FarmPro Balance */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-[#0072CE] rounded-full flex items-center justify-center text-xs font-bold text-white">
                FP
              </div>
              <span className="text-slate-400 text-sm font-semibold">FarmPro</span>
            </div>
            <p className="text-white text-2xl font-bold">
              {formatBalance(ogbBalance, 18, 'FP')}
            </p>
            <p className="text-slate-500 text-xs mt-2">Governance token</p>
          </div>
        </motion.div>

        {/* AGC: Payment History Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">
            Payment History
          </h3>

          {isLoadingHistory ? (
            <div className="bg-slate-800/50 rounded-xl p-8 text-center border border-slate-700/50">
              <p className="text-slate-400">Loading payment history...</p>
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="bg-slate-800/50 rounded-xl p-8 text-center border border-slate-700/50">
              <p className="text-slate-400">No payment history yet</p>
              <p className="text-slate-500 text-sm mt-2">
                Revenue-sharing payments will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {paymentHistory.map((payment) => (
                <div
                  key={payment.id}
                  className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 hover:border-[#0072CE]/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold">{payment.description}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          payment.type === 'revenue_share' 
                            ? 'bg-[#0072CE]/20 text-[#0072CE]'
                            : payment.type === 'staking_reward'
                            ? 'bg-[#0072CE]/20 text-[#0072CE]'
                            : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {payment.type.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm">
                        {formatTimestamp(payment.timestamp)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold text-lg">
                        +{payment.amount} {payment.token}
                      </p>
                      {payment.txHash && (
                        <a
                          href={getExplorerUrl(payment.txHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0072CE] text-xs hover:underline flex items-center gap-1 mt-1"
                        >
                          View TX
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </main>
  )
}

